import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiEdit2, FiExternalLink, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import { API_BASE_URL, authFetch, authFetchForm } from "../../api";

const inputClass = "w-full px-3 py-1.5 rounded-md bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const cardClass = "bg-navy border border-navy-border rounded-xl p-6";

// The texts of each section of the page (the repeated items - stats, features, modules, plans - are edited in lists beside them).
const SECTION_TEXTS = {
  top: { title: "Top of the page", fields: [
    { key: "hero_badge", label: "Small badge above the title" },
    { key: "title_line1", label: "Title, first line" },
    { key: "title_line2", label: "Title, second line (in the brand gradient)" },
    { key: "subtitle", label: "Text under the title", area: true },
    { key: "cta_primary_text", label: "First button (goes to the quote form)" },
    { key: "cta_secondary_text", label: "Second button" },
    { key: "login_url", label: "Second button's address (leave empty to hide the button)", wide: true },
    { key: "nav_links", label: "Menu under the top (one per line: label|section - the sections are features, modules, pricing, quote)", area: true },
  ] },
  features: { title: "Core features - heading", fields: [
    { key: "features_tag", label: "Small tag" },
    { key: "features_title", label: "Heading" },
    { key: "features_subtitle", label: "Text under the heading", area: true },
  ] },
  modules: { title: "Modules - heading", fields: [
    { key: "modules_tag", label: "Small tag" },
    { key: "modules_title", label: "Heading" },
  ] },
  pricing: { title: "Pricing - heading and labels", fields: [
    { key: "pricing_tag", label: "Small tag" },
    { key: "pricing_title", label: "Heading" },
    { key: "pricing_subtitle", label: "Text under the heading", area: true },
    { key: "price_period", label: "Written under each price (the currency code follows it)" },
    { key: "currency_label", label: "Beside the currency chooser" },
    { key: "popular_badge_text", label: "Badge on the most popular plan" },
    { key: "plan_button_text", label: "Button of each plan ({plan} is replaced by the plan's name)" },
  ] },
  quote: { title: "Quote - texts and form", fields: [
    { key: "quote_tag", label: "Small tag" },
    { key: "quote_title", label: "Heading" },
    { key: "quote_text", label: "Text under the heading", area: true },
    { key: "quote_points", label: "Points on the left (one per line: icon|title|description)", area: true },
    { key: "trust_points", label: "Small reassurances under them (one per line: icon|text)", area: true },
    { key: "quote_form_title", label: "Form heading" },
    { key: "quote_button_text", label: "Send button" },
    { key: "quote_custom_option", label: "Last choice of the package list" },
    { key: "quote_success_title", label: "After sending: heading" },
    { key: "quote_success_text", label: "After sending: text ({email} is replaced by the visitor's address)", area: true },
    { key: "quote_again_text", label: "After sending: button to send another" },
  ] },
};

const TABS = ["Top of the page", "Stats", "Core features", "Modules", "Pricing", "Quote"];

// ---- one editable list: stats, features, modules, plans, currencies -----------------------------------------

function ListEditor({ title, help, endpoint, pageId, fields, columns, blank, onChange, canAdd = true }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);        // null | "new" | an item's id
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    authFetch(`/core/${endpoint}/?page=${pageId}`).then((r) => r.json()).then(setItems).catch(() => setError("Failed to load."));
  }, [endpoint, pageId]);
  useEffect(load, [load]);

  const open = (item) => {
    setError("");
    setFile(null);
    setEditing(item ? item.id : "new");
    setForm(item ? { ...blank, ...item } : { ...blank, order: items.length });
  };
  const close = () => { setEditing(null); setFile(null); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    const fd = new FormData();
    fields.forEach((f) => { if (f.type !== "file") fd.append(f.key, form[f.key] ?? ""); });
    fd.append("page", pageId);
    const fileField = fields.find((f) => f.type === "file");
    if (fileField && file) fd.append(fileField.key, file);
    const res = await authFetchForm(editing === "new" ? `/core/${endpoint}/` : `/core/${endpoint}/${editing}/`, fd, editing === "new" ? "POST" : "PATCH");
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return setError(Object.values(body).flat().join(" ") || "Could not save.");
    }
    close();
    load();
    onChange && onChange();
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this?")) return;
    await authFetch(`/core/${endpoint}/${item.id}/`, { method: "DELETE" });
    if (editing === item.id) close();
    load();
    onChange && onChange();
  };

  return (
    <div className={cardClass}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          {help && <p className="text-xs text-gray-500 mt-1 max-w-2xl">{help}</p>}
        </div>
        {canAdd && editing === null && (
          <button type="button" onClick={() => open(null)} className="shrink-0 px-3 py-1.5 rounded-md bg-brand-gradient text-white text-sm font-semibold flex items-center gap-1.5"><FiPlus /> Add</button>
        )}
      </div>

      {editing !== null && (
        <form onSubmit={save} className="mb-5 rounded-lg border border-accent-light/30 bg-white/[0.03] p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">{editing === "new" ? "Add new" : "Edit"}</h3>
            <button type="button" onClick={close} className="text-xs text-gray-400 hover:text-white flex items-center gap-1"><FiX size={14} /> Cancel</button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.key} className={f.wide || f.area || f.type === "file" ? "md:col-span-2" : ""}>
                {f.type === "checkbox" ? (
                  <label className="flex items-center gap-2 text-sm text-gray-300 mt-5">
                    <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} /> {f.label}
                  </label>
                ) : f.type === "file" ? (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
                    {(file || form[f.key]) && <img src={file ? URL.createObjectURL(file) : form[f.key]} alt="" className="w-48 rounded-md border border-navy-border mb-2" />}
                    <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0] || null)} className="text-xs text-gray-400" />
                  </div>
                ) : (
                  <>
                    <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
                    {f.area ? (
                      <textarea className={`${inputClass} min-h-[90px] resize-y`} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                    ) : (
                      <input
                        type={f.type === "color" ? "color" : f.type === "number" ? "number" : "text"}
                        step={f.step}
                        className={f.type === "color" ? "h-9 w-20 rounded-md bg-transparent border border-navy-border" : inputClass}
                        value={form[f.key] ?? ""}
                        required={f.required}
                        placeholder={f.placeholder}
                        onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
                      />
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          {error && <p className="text-sm text-red-400 mt-3">{error}</p>}
          <button className="mt-4 px-4 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold">{editing === "new" ? "Add" : "Save changes"}</button>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr>
              {columns.map((c) => <th key={c.key} className="px-3 py-2">{c.label}</th>)}
              <th className="px-3 py-2 w-24">Shown</th>
              <th className="px-3 py-2 w-40" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-navy-border align-middle">
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-gray-200">
                    {c.image ? (item[c.key] ? <img src={item[c.key]} alt="" className="w-16 h-12 object-cover rounded border border-navy-border" /> : <span className="text-gray-600 text-xs">built-in</span>) : c.color ? <span className="inline-block w-5 h-5 rounded align-middle border border-white/20" style={{ background: item[c.key] }} /> : (/^-?\d+\.\d+$/.test(String(item[c.key])) ? String(Number(item[c.key])) : String(item[c.key] ?? "")).slice(0, 70)}
                  </td>
                ))}
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${item.is_active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-gray-500"}`}>{item.is_active ? "Yes" : "Hidden"}</span></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => open(item)} className="p-1.5 rounded-md text-accent-light hover:bg-accent-light/10 flex items-center gap-1 text-xs"><FiEdit2 size={14} /> Edit</button>
                    <button type="button" onClick={() => remove(item)} className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10 flex items-center gap-1 text-xs"><FiTrash2 size={14} /> Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 2} className="px-3 py-6 text-center text-gray-500">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- the prices that visitors will see, worked out from the dollar prices ---------------------------------------

function PricePreview({ systemId, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE_URL}/core/systems/${systemId}/page/`).then((r) => r.json()).then(setData).catch(() => setData(false));
  }, [systemId, refreshKey]);

  return (
    <div className={cardClass}>
      <h2 className="text-lg font-semibold">Prices visitors will see</h2>
      <p className="text-xs text-gray-500 mt-1 mb-4 max-w-2xl">
        Worked out for you: a plan's dollar price &times; the currency's rate, rounded to the nearest multiple of its rounding step.
        Each visitor sees the row of the country their IP address is in (DEFAULT when their country is not listed). Change a dollar price or a rate above and this table follows.
      </p>
      {data === false && <p className="text-sm text-red-400">Could not load the preview.</p>}
      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-gray-400 text-left">
              <tr>
                <th className="px-3 py-2">Country</th>
                <th className="px-3 py-2">Currency</th>
                {data.plans.map((p) => <th key={p.id} className="px-3 py-2">{p.name} <span className="text-gray-500 font-normal">(${p.price_usd})</span></th>)}
              </tr>
            </thead>
            <tbody>
              {data.currencies.map((c) => (
                <tr key={c.country_code} className="border-t border-navy-border">
                  <td className="px-3 py-2 text-gray-200">{c.country_code}</td>
                  <td className="px-3 py-2 text-gray-400">{c.currency_code}</td>
                  {data.plans.map((p) => <td key={p.id} className="px-3 py-2 text-white">{c.symbol}{Number(p.prices[c.country_code]).toLocaleString("en-US", { maximumFractionDigits: 2 })}</td>)}
                </tr>
              ))}
              {(data.currencies.length === 0 || data.plans.length === 0) && <tr><td colSpan={data.plans.length + 2} className="px-3 py-6 text-center text-gray-500">Add at least one plan and one currency.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- the texts of one section of the page -------------------------------------------------------------------

function TextsForm({ page, section, onSaved }) {
  const group = SECTION_TEXTS[section];
  const [form, setForm] = useState(page);
  const [message, setMessage] = useState("");

  const save = async (e) => {
    e.preventDefault();
    const body = {};
    group.fields.forEach((f) => { body[f.key] = form[f.key] ?? ""; });
    const res = await authFetch(`/core/system-pages/${page.id}/`, { method: "PATCH", body: JSON.stringify(body) });
    setMessage(res.ok ? "Saved." : "Could not save.");
    if (res.ok) onSaved(await res.json());
  };

  return (
    <form onSubmit={save} className={cardClass}>
      <h2 className="text-lg font-semibold mb-4">{group.title}</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {group.fields.map((f) => (
          <div key={f.key} className={f.area || f.wide ? "md:col-span-2" : ""}>
            <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
            {f.area
              ? <textarea className={`${inputClass} min-h-[90px] resize-y`} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              : <input className={inputClass} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-5">
        <button className="px-5 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold">Save</button>
        {message && <span className={`text-sm ${message === "Saved." ? "text-emerald-400" : "text-red-400"}`}>{message}</span>}
      </div>
    </form>
  );
}

// ---- the screen ---------------------------------------------------------------------------------------------

const STAT_FIELDS = [
  { key: "value", label: "Number", type: "number", required: true },
  { key: "suffix", label: "After the number (+, %, /7 ...)" },
  { key: "label", label: "Label", wide: true, required: true },
  { key: "order", label: "Position", type: "number" },
  { key: "is_active", label: "Shown on the page", type: "checkbox" },
];
const FEATURE_FIELDS = [
  { key: "icon", label: "Icon (an emoji)" },
  { key: "title", label: "Title", required: true },
  { key: "description", label: "Text", area: true },
  { key: "image", label: "Screenshot (shown whole, 4:3 works best)", type: "file" },
  { key: "image_path", label: "Or the address of a picture that ships with the website", wide: true, placeholder: "/images/erp/dashboard.png" },
  { key: "order", label: "Position", type: "number" },
  { key: "is_active", label: "Shown on the page", type: "checkbox" },
];
const MODULE_FIELDS = [
  { key: "icon", label: "Icon (an emoji)" },
  { key: "title", label: "Title", required: true },
  { key: "color", label: "Colour", type: "color" },
  { key: "description", label: "Text", area: true },
  { key: "order", label: "Position", type: "number" },
  { key: "is_active", label: "Shown on the page", type: "checkbox" },
];
const PLAN_FIELDS = [
  { key: "name", label: "Name", required: true },
  { key: "price_usd", label: "Price in US dollars (every other currency is worked out from this)", type: "number", step: "0.01", required: true },
  { key: "period_label", label: "Written under the price (per month, per year, one-time license ...; empty uses the page's own wording)" },
  { key: "features", label: "What is included (one per line)", area: true },
  { key: "is_popular", label: "Mark as most popular", type: "checkbox" },
  { key: "order", label: "Position", type: "number" },
  { key: "is_active", label: "Shown on the page", type: "checkbox" },
];
const CURRENCY_FIELDS = [
  { key: "country_code", label: "Country code (PK, US ... or DEFAULT)", required: true },
  { key: "currency_code", label: "Currency code (PKR, USD ...)", required: true },
  { key: "symbol", label: "Symbol", required: true },
  { key: "name", label: "Currency name" },
  { key: "rate", label: "Rate: how many of this currency make 1 US dollar (280 for rupees, 1 for dollars)", type: "number", step: "any", required: true },
  { key: "round_to", label: "Round prices to the nearest (e.g. 100 for rupees, 1 for dollars)", type: "number", step: "any", required: true },
  { key: "order", label: "Position", type: "number" },
  { key: "is_active", label: "Offered on the page", type: "checkbox" },
];

export default function SystemPageEditor() {
  const { id } = useParams();
  const [system, setSystem] = useState(null);
  const [page, setPage] = useState(undefined);          // undefined = loading, null = none yet
  const [tab, setTab] = useState(0);
  const [priceKey, setPriceKey] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    authFetch(`/core/systems/${id}/`).then((r) => r.json()).then(setSystem).catch(() => setError("Failed to load the system."));
    authFetch(`/core/system-pages/?system=${id}`).then((r) => r.json()).then((list) => setPage(list[0] || null)).catch(() => setError("Failed to load the page."));
  }, [id]);

  const create = async () => {
    const res = await authFetch("/core/system-pages/", { method: "POST", body: JSON.stringify({ system: Number(id) }) });
    if (res.ok) setPage(await res.json());
    else setError("Could not create the page.");
  };

  return (
    <div className="w-full">
      <Link to="/admin/pages/home/systems" className="text-xs text-accent-light hover:underline">&larr; Back to Systems</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-2 mb-2">
        <h1 className="text-2xl font-bold">{system ? system.heading : "System"} - page</h1>
        {page && <a href={`/system/${id}`} target="_blank" rel="noopener noreferrer" className="text-sm text-accent-light hover:underline flex items-center gap-1"><FiExternalLink /> View the page</a>}
      </div>
      <p className="text-sm text-gray-400 mb-6">
        Everything on this system's own page - the one opened by its button without an address - is edited here. Quote requests sent from the page are listed under Enquiries &rarr; Quote Requests.
      </p>
      {error && <p className="text-red-400 mb-4">{error}</p>}

      {page === null && (
        <div className={cardClass}>
          <p className="text-sm text-gray-300 mb-4">This system has no page of its own yet.</p>
          <button type="button" onClick={create} className="px-4 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold">Create the page</button>
          <p className="text-xs text-gray-500 mt-3">To fill it with the prepared content of mraaliERP, EMI, POS, CRM or BMS instead, run <code>python manage.py seed_system_pages</code> on the server.</p>
        </div>
      )}

      {page && (
        <>
          <div className="flex flex-wrap gap-1 mb-6 border-b border-navy-border" role="tablist">
            {TABS.map((t, i) => (
              <button key={t} type="button" role="tab" aria-selected={tab === i} onClick={() => setTab(i)}
                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === i ? "border-accent-light text-accent-light" : "border-transparent text-gray-400 hover:text-white"}`}>{t}</button>
            ))}
          </div>

          <div className="space-y-6">
            {tab === 0 && (
              <>
                <TextsForm page={page} section="top" onSaved={setPage} />
                <p className="text-xs text-gray-500">
                  The card that turns through the modules under the title is made from the Modules tab. The two buttons of this system on the home page
                  are set in <Link to="/admin/pages/home/systems" className="text-accent-light hover:underline">Systems</Link> (Button 1 and Button 2).
                </p>
              </>
            )}
            {tab === 1 && (
              <ListEditor title="Stats" help="The numbers in the coloured bar under the top of the page. They count up when they scroll into view." endpoint="system-stats" pageId={page.id}
                fields={STAT_FIELDS} blank={{ value: 0, suffix: "", label: "", order: 0, is_active: true }}
                columns={[{ key: "value", label: "Number" }, { key: "suffix", label: "After" }, { key: "label", label: "Label" }, { key: "order", label: "Position" }]} />
            )}
            {tab === 2 && (
              <>
                <TextsForm page={page} section="features" onSaved={setPage} />
                <ListEditor title="Core features" help="The tabs of the core features block. Each has a screenshot, shown whole next to the tabs." endpoint="system-features" pageId={page.id}
                  fields={FEATURE_FIELDS} blank={{ icon: "", title: "", description: "", image: null, image_path: "", order: 0, is_active: true }}
                  columns={[{ key: "image", label: "Screenshot", image: true }, { key: "icon", label: "Icon" }, { key: "title", label: "Title" }, { key: "order", label: "Position" }]} />
              </>
            )}
            {tab === 3 && (
              <>
                <TextsForm page={page} section="modules" onSaved={setPage} />
                <ListEditor title="Modules" help="The cards of the modules block. The same modules also turn through the card at the top of the page." endpoint="system-modules" pageId={page.id}
                  fields={MODULE_FIELDS} blank={{ icon: "", title: "", color: "#22D3EE", description: "", order: 0, is_active: true }}
                  columns={[{ key: "icon", label: "Icon" }, { key: "title", label: "Title" }, { key: "color", label: "Colour", color: true }, { key: "order", label: "Position" }]} />
              </>
            )}
            {tab === 4 && (
              <>
                <TextsForm page={page} section="pricing" onSaved={setPage} />
                <ListEditor title="Plans - one price, in US dollars" help="The packages of the pricing block. Enter each price once, in US dollars: the price in every other currency is worked out from it." endpoint="system-plans" pageId={page.id} onChange={() => setPriceKey((k) => k + 1)}
                  fields={PLAN_FIELDS} blank={{ name: "", price_usd: 0, period_label: "", features: "", is_popular: false, order: 0, is_active: true }}
                  columns={[{ key: "name", label: "Name" }, { key: "price_usd", label: "Price (USD)" }, { key: "period_label", label: "Period" }, { key: "is_popular", label: "Popular" }]} />
                <ListEditor title="Countries, currencies and rates" help="Each visitor sees prices in the currency of the country their IP address is in. A currency's rate says how many of it make one US dollar; DEFAULT (usually dollars) is used when the visitor's country is not listed. Add a country and its prices appear at once." endpoint="system-currencies" pageId={page.id} onChange={() => setPriceKey((k) => k + 1)}
                  fields={CURRENCY_FIELDS} blank={{ country_code: "", currency_code: "", symbol: "", name: "", rate: 1, round_to: 1, order: 0, is_active: true }}
                  columns={[{ key: "country_code", label: "Country" }, { key: "currency_code", label: "Currency" }, { key: "symbol", label: "Symbol" }, { key: "rate", label: "Rate per $1" }, { key: "round_to", label: "Round to" }]} />
                <PricePreview systemId={id} refreshKey={priceKey} />
              </>
            )}
            {tab === 5 && (
              <>
                <TextsForm page={page} section="quote" onSaved={setPage} />
                <p className="text-xs text-gray-500">Requests sent from this form are listed under Enquiries &rarr; Quote Requests.</p>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
